import React, { useCallback, useEffect, useRef, useState } from 'react'
import { arrayMove, SortableContainer, SortableElement } from 'react-sortable-hoc'
import { Checkbox } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { setCodecPreferences } from 'store/actions/settings'

import cn from 'classnames'
import { isSameCodec } from 'common/RTCUtils'
import { useTranslation } from 'react-i18next'

const Codec = SortableElement(({ item, onChange, t }) => {
    return (
        <tr>
            <td>
                <span>{item.name}</span>
            </td>
            <td>
                <span>
                    {item.rate} {t('hertz')} {item.channels > 1 && `/ ${item.channels}`}
                </span>
            </td>
            <td>
                <Checkbox checked={item.enabled} onChange={onChange}></Checkbox>
            </td>
        </tr>
    )
})

const CodecsList = SortableContainer(({ t, items: codecItems, setCodecs, forwardedRef }) => {
    const toggleCodecEnabled = (e, index) =>
        setCodecs((codecs) => {
            return codecs.map((item, i) => {
                return i === index ? { ...item, enabled: e.target.checked } : item
            })
        })

    return (
        <table className="codecs-list">
            <thead>
                <tr>
                    <th>{t('codecName')}</th>
                    <th className="text-nowrap">{t('codecRate')}</th>
                    <th></th>
                </tr>
            </thead>
            <tbody ref={forwardedRef}>
                {codecItems.map((item, index) => {
                    return (
                        <Codec
                            key={`item-${index}`}
                            index={index}
                            item={item}
                            onChange={(e) => toggleCodecEnabled(e, index)}
                            t={t}
                        />
                    )
                })}
            </tbody>
        </table>
    )
})

export const CodecsBlock = () => {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const [codecs, setCodecs] = useState([])
    const [codecsInitialized, setCodecsInitialized] = useState(false)
    const [supportedCodecs, setSupportedCodecs] = useState([])

    const { codecPreferences } = useSelector((state) => state.settings)
    const { codecCapabilities } = useSelector((state) => state.userAgent)
    const { globalCodecPrefs } = useSelector((state) => state.profile)

    const tabelBodyRef = useRef(null)

    const enableCodecsByDefault = (codecs) => {
        return codecs.map((codec) => ({ ...codec, enabled: true }))
    }

    const getSuportedCodecs = (browserSupported, serverSupported) => {
        const supportedCodecs = []

        if (serverSupported.length) {
            serverSupported.forEach((codec) => {
                const bothSupported = browserSupported.filter(
                    (browserCodec) => browserCodec.name.toLowerCase() === codec.toLowerCase()
                )
                supportedCodecs.push(...bothSupported)
            })
        } else {
            supportedCodecs.push(...browserSupported)
        }

        return supportedCodecs
    }

    const prepareCodecList = (prefecences, supported) => {
        if (prefecences?.length) {
            /* Filter out not supported items existing at preferences */
            const preferencesSupported = prefecences.filter((prefItem) =>
                supported.some((suppItem) => isSameCodec(prefItem, suppItem))
            )

            /* Filter out supported items that are not on the preferences list */
            const restSupported = supported.filter(
                (suppItem) => !preferencesSupported.some((prefItem) => isSameCodec(prefItem, suppItem))
            )

            return [...preferencesSupported, ...restSupported]
        } else {
            return enableCodecsByDefault(supported)
        }
    }

    useEffect(() => {
        if (codecCapabilities.length) {
            const supported = getSuportedCodecs(codecCapabilities, globalCodecPrefs)
            setSupportedCodecs(supported)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codecCapabilities, globalCodecPrefs])

    useEffect(() => {
        if (supportedCodecs.length) {
            setCodecs(prepareCodecList(codecPreferences, supportedCodecs))
            setCodecsInitialized(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supportedCodecs])

    const onSortEnd = useCallback(({ oldIndex, newIndex }) => {
        setCodecs((oldItems) => arrayMove(oldItems, oldIndex, newIndex))
    }, [])

    const updateCodecsPreferences = (codecs) => {
        dispatch(setCodecPreferences(codecs))
    }

    useEffect(() => {
        codecsInitialized && updateCodecsPreferences(codecs)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codecs])

    return (
        <div className={cn('sound-and-devices', 'has-margin-bottom')}>
            <span className="sound-and-devices__title">{t('codecs')}</span>
            <div className="sound-and-devices__item">
                <p className="sound-and-devices__item--description font-weight-light">{t('codecsDescription')}</p>
                <CodecsList
                    t={t}
                    items={codecs}
                    onSortEnd={onSortEnd}
                    setCodecs={setCodecs}
                    helperClass="trActive"
                    forwardedRef={tabelBodyRef}
                    helperContainer={tabelBodyRef.current}
                />
            </div>
        </div>
    )
}
